import { Toast } from '@unmind/design-system';
import React, { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { defaultLightTheme, telusPrototypeTheme } from 'styles';
import { ThemeProvider } from 'styled-components';
import { Clarity } from 'Shared';
import { isTelusTheme } from 'utils/getChannel';
import { initialiseMSTeamsApp } from 'utils/MSTeams';
import { getPageDirectionFromLocale } from 'i18n/getPageDirectionFromLocale';
import { getUserLocale } from 'i18n/getUserLocale';
import createFeatureFlagProvider from '../flags/createFeatureFlagProvider';
import { useUpdateDbUserLocaleIfNeeded } from '../i18n/updateDbUserLocaleIfNeeded';
import UniversalLoginProvider from '../LoggedOut/UniversalLogin/UniversalLoginProvider';
import AuthorizedApolloProvider from './Auth/AuthorizedApolloProvider';
import LocalAuthStatus, { AuthState } from './Auth/LocalAuthStatus';
import RefreshAuthToken from './Auth/RefreshAuthToken';
import Braze from './braze/Braze';
import { openBrazeSession } from './braze/identifyBrazeUser';
import { initialiseBrazeInAppMessaging } from './braze/initialiseBrazeInAppMessaging';
import { Layout, Meta } from './components';
import { DatadogSetup } from './Datadog';
import GlobalStyle from './GlobalStyle';
import { logDevOnlyInfo } from './logging';
import Router from './Router';
import Route404 from './Router/Route404/index';
import {
  initialiseAmplitude,
  tracking,
  trackUtmParamsIfAvailable,
} from './Tracking';

const Authenticating = () => null;

initialiseAmplitude();

tracking.track('Application Opened');

const { pathname, search } = window.location;
trackUtmParamsIfAvailable({ pathname, search });

const FeatureFlagProvider = createFeatureFlagProvider({
  key: process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_KEY as string,
});

const pageDirection = getPageDirectionFromLocale(getUserLocale());
void initialiseMSTeamsApp();

interface AuthEffectsProps {
  authState: AuthState;
}

const AuthEffects = ({ authState }: AuthEffectsProps) => {
  const { state, isAuthenticated } = authState;
  const { updateDbUserLocaleIfNeeded, loading, hasBeenUpdated } =
    useUpdateDbUserLocaleIfNeeded();

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (state === 'READY' && isAuthenticated) {
      /**
       * This will trigger when a user signs in,
       * or an authenticated user opens/refreshes the app
       */
      logDevOnlyInfo(`isAuthenticated: ${isAuthenticated}`);
      // Braze InApp messaging must be initialised before openBrazeSession();
      initialiseBrazeInAppMessaging();
      openBrazeSession();

      if (!loading && !hasBeenUpdated) {
        void updateDbUserLocaleIfNeeded();
      }
    }
  }, [
    state,
    isAuthenticated,
    loading,
    hasBeenUpdated,
    updateDbUserLocaleIfNeeded,
  ]);

  if (state === 'READY' && isAuthenticated && token) {
    return <RefreshAuthToken />;
  }

  return null;
};

const theme = isTelusTheme() ? telusPrototypeTheme : defaultLightTheme;

const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <FeatureFlagProvider>
        <ThemeProvider
          // @ts-ignore Styled Components theme expects Colors, but we want to use custom values too for Telus prototype
          theme={{
            ...theme,
            direction: pageDirection,
          }}
        >
          <UniversalLoginProvider>
            <AuthorizedApolloProvider>
              <Braze>
                <Layout>
                  <Meta />
                  <DatadogSetup />
                  <Toast />
                  <LocalAuthStatus>
                    {authState => <AuthEffects authState={authState} />}
                  </LocalAuthStatus>
                  <GlobalStyle />
                  <Clarity />
                  <Router
                    renderIfAuthenticating={Authenticating}
                    renderIfUnauthorized={Route404}
                  />
                </Layout>
              </Braze>
            </AuthorizedApolloProvider>
          </UniversalLoginProvider>
        </ThemeProvider>
      </FeatureFlagProvider>
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
